import type { ReactNode } from 'react';
import { ApiError } from '../api/client.js';
import { Chapi, type ChapiPose } from './Chapi.js';

/** `art` replaces the pose with an animated moment (docs/designs/motion), e.g. Chapi asleep when all up to date. */
export function Empty({ title, children, pose = 'happy', action, art }: { title: string; children?: ReactNode; pose?: ChapiPose; action?: ReactNode; art?: ReactNode }) {
  return (
    <div className="state" role="status">
      {art ?? <Chapi pose={pose} size={84} />}
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

/**
 * The empty state inside a side panel or card ("More polish to do" 8): a small Chapi pose, one line and one clear action,
 * sized for a 340 px panel.
 */
export function PanelEmpty({ children, pose = 'happy', action }: { children: ReactNode; pose?: ChapiPose; action?: ReactNode }) {
  return (
    <div className="gp-empty panel-empty">
      <Chapi pose={pose} size={48} />
      <p>{children}</p>
      {action}
    </div>
  );
}

export function Loading({ rows = 4, height = 44 }: { rows?: number; height?: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-12)', padding: 'var(--space-16)' }} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ height, opacity: 1 - i * 0.12 }} />
      ))}
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const offline = error instanceof ApiError && error.code === 'network';
  const message = error instanceof ApiError ? error.message : 'Something went wrong.';
  return (
    <div className="state" role="alert">
      <Chapi pose={offline ? 'sleeping' : 'oops'} size={84} />
      <h3>{offline ? 'You are offline' : 'That did not load'}</h3>
      <p>{message}</p>
      {retry && (
        <button className="btn btn-sm" onClick={retry}>
          Try again
        </button>
      )}
    </div>
  );
}

/** Renders loading, error or content for a query. */
export function QueryView<T>({ query, children, loading }: { query: { data: T | undefined; error: unknown; isPending: boolean; refetch: () => unknown }; children: (data: T) => ReactNode; loading?: ReactNode }) {
  if (query.data !== undefined) return <>{children(query.data)}</>;
  if (query.isPending) return <>{loading ?? <Loading />}</>;
  return <ErrorState error={query.error} retry={() => void query.refetch()} />;
}
